import React from 'react';
import { Link, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useArticle } from '../../hooks/useArticles';
import ReadingProgress from '../ReadingProgress/ReadingProgress';
import './AirtableBlogSingle.css';

const AirtableBlogSingle = () => {
    const { slug } = useParams();
    const { article, loading, error } = useArticle(slug);

    // Clean up markdown content - fix malformed image syntax and remove backslashes
    const cleanContent = (content) => {
        if (!content) return '';

        // Remove backslashes followed by newlines or spaces
        let cleaned = content.replace(/\\\n/g, '').replace(/\\\s/g, ' ');

        // Fix malformed image markdown: ![alt]\n\nhttp://url to ![alt](http://url)
        cleaned = cleaned.replace(/!\[([^\]]*)\]\s*\n+\s*(https?:\/\/[^\s\n]+)/g, '![$1]($2)');

        return cleaned;
    };

    if (loading) {
        return (
            <section className="wpo-blog-single-section section-padding">
                <div className="container">
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status" style={{width: '3rem', height: '3rem'}}>
                            <span className="visually-hidden">Loading article...</span>
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    if (error || !article) {
        return (
            <section className="wpo-blog-single-section section-padding">
                <div className="container">
                    <div className="alert alert-danger" role="alert">
                        {error || 'Article not found'}
                    </div>
                    <div className="text-center mt-4">
                        <Link to="/blog" className="theme-btn">
                            Back to Blog
                        </Link>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <>
            <ReadingProgress />
            <section className="wpo-blog-single-section section-padding">
                <div className="container">
                <div className="row justify-content-center">
                    <div className="col-lg-10 col-12">
                        <div className="wpo-blog-content">
                            <article className="blog-post-single">
                                {/* Article Meta */}
                                <div className="post-meta">
                                    <div className="meta-item">
                                        <i className="flaticon-user"></i>
                                        <span>{article.author || 'Dr. Eugene Pereira'}</span>
                                    </div>
                                    {article.readTime && (
                                        <div className="meta-item">
                                            <i className="flaticon-deadline"></i>
                                            <span>{article.readTime.toString().includes('min') ? article.readTime : `${article.readTime} mins`}</span>
                                        </div>
                                    )}
                                </div>

                                {/* Article Content - Markdown Parsed */}
                                <div className="post-content markdown-content">
                                    <ReactMarkdown
                                        remarkPlugins={[remarkGfm]}
                                        components={{
                                            img: ({node, ...props}) => (
                                                <img
                                                    {...props}
                                                    style={{maxWidth: '100%', height: 'auto', margin: '2rem 0'}}
                                                    loading="lazy"
                                                />
                                            )
                                        }}
                                    >
                                        {cleanContent(article.content)}
                                    </ReactMarkdown>
                                </div>

                                {/* Author Info - Dr. Eugene Pereira */}
                                <div className="post-author-card">
                                    <div className="author-avatar">
                                        <img
                                            src="/images/team/dr-eugene.jpg"
                                            alt="Dr. Eugene Pereira"
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                            }}
                                        />
                                    </div>
                                    <div className="author-info">
                                        <h4 className="author-name">Dr. Eugene Pereira</h4>
                                        <p className="author-title">Medical Director</p>
                                        <p className="author-bio">
                                            Dr. Eugene Pereira is a board-certified physician specializing in regenerative
                                            medicine and advanced treatment protocols. With over 20 years of experience,
                                            he leads Amped Therapeutics in providing cutting-edge medical solutions.
                                        </p>
                                    </div>
                                </div>

                                {/* Navigation */}
                                <div className="post-navigation">
                                    <Link to="/blog" className="back-to-blog">
                                        <i className="flaticon-left-arrow"></i>
                                        Back to All Articles
                                    </Link>
                                </div>
                            </article>
                        </div>
                    </div>
                </div>
            </div>
        </section>
        </>
    );
};

export default AirtableBlogSingle;